/**
 * Admin People RPC definitions
 *
 * A thin admin-side people surface. It exposes the dedupe `search` (match by
 * email OR phone) the customer-onboarding flow uses before creating a new
 * person — each hit carrying its primary `customerId` for the admin global
 * search — and the GDPR right-to-erasure `erase` cascade. All endpoints
 * require admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { DatabaseError as DbDatabaseError } from "@fm/db/errors";
import { Person } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

/**
 * Input for `search` — substring match on a person's email OR phone
 * (case-insensitive). An empty/whitespace query returns the empty list.
 *
 * @since 1.0.0
 */
export const PeopleSearchInput = Schema.Struct({
  query: Schema.String,
});

/**
 * Input for `erase` — the id of the person to run the GDPR cascade erasure
 * against.
 *
 * @since 1.0.0
 */
export const PersonEraseInput = Schema.Struct({
  personId: Schema.String,
});

/**
 * Outcome of an `erase` run — the wire shape of the db layer's
 * `ErasePersonResult` audit counts (rows hard-deleted vs. rows retained with
 * person links anonymized).
 *
 * @since 1.0.0
 */
export const PersonEraseResult = Schema.Struct({
  personId: Schema.NullOr(Schema.String),
  personFound: Schema.Boolean,
  deleted: Schema.Struct({
    conversations: Schema.Number,
    personas: Schema.Number,
    quests: Schema.Number,
    researchOutlines: Schema.Number,
    researchExecutions: Schema.Number,
    sectionResearches: Schema.Number,
    compiledReports: Schema.Number,
    leads: Schema.Number,
    customerMembers: Schema.Number,
    accessGrants: Schema.Number,
  }),
  anonymized: Schema.Struct({
    customers: Schema.Number,
    intakesPrimary: Schema.Number,
    intakesCreatedBy: Schema.Number,
    intakeLeadLinks: Schema.Number,
    leadAssignments: Schema.Number,
    detachedPersonas: Schema.Number,
    detachedQuests: Schema.Number,
  }),
});

/**
 * One `search` hit — the person row plus the id of their primary customer
 * (owner membership first, else the earliest membership), or `null` when they
 * belong to no customer. Lets the admin global search open a PERSON result's
 * customer.
 *
 * @since 1.0.0
 */
export const PersonSearchHit = Schema.extend(
  Person.json,
  Schema.Struct({ customerId: Schema.NullOr(Schema.String) }),
);

/**
 * Admin People RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminPeopleRpcs extends RpcGroup.make(
  Rpc.make("search", {
    payload: PeopleSearchInput,
    success: Schema.Array(PersonSearchHit),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("erase", {
    payload: PersonEraseInput,
    success: PersonEraseResult,
    error: Schema.Union(Unauthorized, Forbidden, DbDatabaseError),
  }).middleware(AdminMiddleware),
).prefix("admin/people/") {}
